import {Dialog, showNotification} from 'platform/components';
import {Box} from 'platform/foundation';

import {useState} from 'react';

import {isNil} from 'ramda';

import {useCreateBusinessCaseMutation, useLinkTradeInMutation} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {businessCaseRoutes, testIds} from '@dms/routes';
import {DmsDataGrid} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import {handleSaleVehicleSelectionError} from '../../BusinessCaseOffers/BusinessCaseSaleVehicles/OfferAlternatives/utils/handleOfferAlternativeError';

const SELECT_VEHICLE_GRID = 'business-case-add-vehicle';

interface TradeInCreateSellingDialogProps {
  businessCaseId: string;
  hostCustomerId: string;
  hostBranchId: string;
  onClose: () => void;
}

export function TradeInCreateSellingDialog(props: TradeInCreateSellingDialogProps) {
  const navigate = useNavigate();
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>();
  const [createBusinessCase] = useCreateBusinessCaseMutation();
  const [linkTradeIn] = useLinkTradeInMutation();

  const handleSubmit = () => {
    if (isNil(selectedVehicleId)) {
      return;
    }
    createBusinessCase({
      xBranch: props.hostBranchId,
      createBusinessCaseRequestBody: {
        customerId: props.hostCustomerId,
        vehicleToSaleId: selectedVehicleId,
      },
    })
      .unwrap()
      .then((newBc) => {
        const navigateToCreatedBusinessCase = () =>
          navigate(composePath(businessCaseRoutes.selling, {params: {id: newBc.id}}));

        // The BC is already created at this point; whether or not the trade-in link
        // succeeds we navigate to it, so the user never stays stranded in the dialog
        // re-submitting and minting duplicate business cases.
        return linkTradeIn({
          businessCaseId: props.businessCaseId,
          linkTradeInRequestBody: {counterpartBusinessCaseId: newBc.id},
          xBranch: props.hostBranchId,
        })
          .unwrap()
          .then(() => {
            showNotification.success(i18n.t('page.businessCase.tradeIn.linkSuccess'));
            navigateToCreatedBusinessCase();
          })
          .catch(() => {
            showNotification.error(
              i18n.t('page.businessCase.tradeIn.linkFailedBusinessCaseCreated')
            );
            navigateToCreatedBusinessCase();
          });
      })
      .catch(handleSaleVehicleSelectionError);
  };

  return (
    <Dialog
      data-testid={testIds.businessCase.detail('createTradeInSelling')}
      size="large"
      isOpen
      onClose={props.onClose}
      title={i18n.t('page.businessCase.tradeIn.createNewSelling')}
      buttons={[
        {title: i18n.t('general.actions.cancel'), variant: 'secondary', onClick: props.onClose},
        {
          title: i18n.t('general.labels.select'),
          variant: 'primary',
          onClick: handleSubmit,
          isDisabled: isNil(selectedVehicleId),
        },
      ]}
    >
      <Box height="60vh">
        <DmsDataGrid
          gridCode={SELECT_VEHICLE_GRID}
          onRowSelectionChange={(rows) => {
            setSelectedVehicleId(rows[0]?.id);
          }}
          data-testid={testIds.businessCase.detail('tradeInSelectVehicleGrid')}
        />
      </Box>
    </Dialog>
  );
}
